import { cn } from './cn';

export type VariantProps<T> = T extends (props: infer P) => any
  ? P extends { variant?: infer V; size?: infer S }
    ? { variant?: V; size?: S }
    : never
  : never;

export interface VariantConfig {
  base?: string;
  variants?: Record<string, Record<string, string>>;
  defaultVariants?: Record<string, string>;
  compoundVariants?: Array<{
    [key: string]: string | string[];
    class: string;
  }>;
}

/**
 * cva - Class Variance Authority (Vue 2 equivalent)
 * Creates a function that returns class names based on variants
 * 
 * Usage:
 * const buttonVariants = cva('base-class', {
 *   variants: {
 *     variant: { default: 'default-class', primary: 'primary-class' },
 *     size: { sm: 'small-class', lg: 'large-class' }
 *   },
 *   defaultVariants: { variant: 'default', size: 'sm' }
 * });
 * 
 * buttonVariants({ variant: 'primary', size: 'lg' })
 */
export function cva(base: string, config?: VariantConfig) {
  const { variants = {}, defaultVariants = {}, compoundVariants = [] } = config || {};

  return (props: Record<string, any> = {}) => {
    const variantProps = { ...defaultVariants, ...props };
    const classes: string[] = [base];

    // Apply variant classes
    Object.entries(variantProps).forEach(([key, value]) => {
      if (variants[key] && variants[key][value]) {
        classes.push(variants[key][value]);
      }
    });

    // Apply compound variants
    compoundVariants.forEach((compound) => {
      const matches = Object.entries(compound).every(([key, values]) => {
        if (key === 'class') return true;
        const propValue = variantProps[key];
        return Array.isArray(values) ? values.includes(propValue) : values === propValue;
      });
      if (matches && compound.class) {
        classes.push(compound.class);
      }
    });

    // Add custom className if provided
    if (props.className) {
      classes.push(props.className);
    }

    return cn(...classes);
  };
}

